import type { ReactNode } from "react";
import { cn } from "@/shared/lib/cn";
import { Popover } from "./Popover";

/** One row in a {@link Menu}. */
export interface MenuItem {
  id: string;
  label: string;
  icon?: ReactNode;
  onSelect: () => void;
  /** Visually separated by extra spacing above (e.g. destructive actions). */
  separated?: boolean;
  disabled?: boolean;
}

export interface MenuProps {
  trigger: (args: { open: boolean; toggle: () => void }) => ReactNode;
  items: MenuItem[];
  side?: "top" | "bottom";
  align?: "start" | "end";
  label: string;
  panelClassName?: string;
}

/**
 * A dropdown menu of actions, built on {@link Popover}. Each item closes the
 * menu before running its action. Monochrome by construction — there is no
 * "danger red"; weight + the confirm dialog carry destructive intent.
 */
export function Menu({ trigger, items, side, align = "end", label, panelClassName }: MenuProps) {
  return (
    <Popover
      label={label}
      side={side}
      align={align}
      panelClassName={panelClassName}
      trigger={({ open, toggle }) => trigger({ open, toggle })}
    >
      {({ close }) => (
        <div role="menu" aria-label={label} className="flex min-h-0 flex-col overflow-y-auto">
          {items.map((item) => (
            <div
              key={item.id}
              className={item.separated ? "mt-2" : undefined}
            >
              <button
                type="button"
                role="menuitem"
                disabled={item.disabled}
                onClick={() => {
                  close();
                  item.onSelect();
                }}
                className={cn(
                  "flex w-full items-center gap-2.5 rounded-sm px-3 py-2 text-left font-sans text-[13.5px] font-medium text-ink outline-none transition-colors duration-150 ease-house hover:bg-line-soft hover:text-ink focus-visible:bg-line-soft disabled:pointer-events-none disabled:opacity-50",
                  "[&_svg]:size-[16px] [&_svg]:shrink-0 [&_svg]:text-ink-soft",
                )}
              >
                {item.icon}
                <span className="min-w-0 flex-1 truncate">{item.label}</span>
              </button>
            </div>
          ))}
        </div>
      )}
    </Popover>
  );
}
